import NProgress from 'nprogress';
import { JSX, useCallback, useEffect, useRef } from 'react';
export { useLoader } from '@/app/hooks/useLoader';

export type LoaderProps = {
  color?: string;
  initialPosition?: number;
  height?: number;
  trickle?: boolean;
  trickleSpeed?: number;
  trickleStopTime?: number;
  easing?: string;
  speed?: number;
};

function LoadingBar({
  color: propColor,
  height: propHeight,
  initialPosition = 0.08,
  trickle = true,
  trickleSpeed = 200,
  trickleStopTime = 10000,
  easing = 'ease',
  speed = 200
}: LoaderProps): JSX.Element {
  const defaultColor = '#29d';
  const defaultHeight = 3;
  const trickleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const color = propColor ?? defaultColor;
  const height = propHeight ?? defaultHeight;

  const styles = (
    <style>
      {`
        #nprogress {
          pointer-events: none
        }

        #nprogress .bar {
          background:${color};
          position: fixed;
          z-index: 1600;
          top: 0;
          left: 0;
          width: 100%;
          height:${height}px
        }

        #nprogress .peg {
          display: block;
          position: absolute;
          right: 0;
          width: 100px;
          height: 100%;
          opacity: 1;
          -webkit-transform: rotate(3deg) translate(0px, -4px);
          -ms-transform: rotate(3deg) translate(0px, -4px);
          transform: rotate(3deg) translate(0px, -4px)
        }

        #nprogress .nprogress-custom-parent {
          overflow: hidden;
          position: relative
        }

        .nprogress-custom-parent #nprogress .bar
        {
          position: absolute
        }
      `}
    </style>
  );

  const toAbsoluteURL = (url: string): string => {
    return new URL(url, window.location.href).href;
  };

  const isSameHostName = useCallback((currentUrl: string, newUrl: string): boolean => {
    const current = new URL(toAbsoluteURL(currentUrl));
    const next = new URL(toAbsoluteURL(newUrl));
    return current.hostname.replace(/^www\./, '') === next.hostname.replace(/^www\./, '');
  }, []);


  function findClosestAnchor(element: HTMLElement | null): HTMLAnchorElement | null {
    while (element && element.tagName.toLowerCase() !== 'a') {
      element = element.parentElement;
    }
    return element as HTMLAnchorElement;
  }

  useEffect(() => {
    NProgress.configure({
      trickle,
      trickleSpeed,
      easing,
      speed,
      minimum: initialPosition,
    });

    if (trickleStopTime && trickle) {
      trickleTimeoutRef.current = setTimeout(() => {
        NProgress.configure({ trickle: false });
      }, trickleStopTime);
    }

    function handleClick(event: MouseEvent): void {
      try {
        const target = event.target as HTMLElement;
        const anchor = findClosestAnchor(target);
        const newUrl = anchor?.href;
        if (newUrl) {
          const currentUrl = window.location.href;
          const isExternalLink = ((anchor as HTMLAnchorElement).target as React.HTMLAttributeAnchorTarget) !== '';

          const isSpecialScheme = ['tel:', 'mailto:', 'sms:', 'blob:', 'download:'].some((scheme) =>
            newUrl.startsWith(scheme)
          );

          const notSameHost = !isSameHostName(window.location.href, anchor.href);
          if (notSameHost) {
            return;
          }
          if (
            newUrl === currentUrl ||
            isExternalLink ||
            isSpecialScheme ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.altKey ||
            !toAbsoluteURL(anchor.href).startsWith('http')
          ) {
            NProgress.start();
            NProgress.done();
          } else {
            NProgress.start();
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('LoaderBar error: ', err);
        }
        NProgress.start();
        NProgress.done();
      }
    }

    ((history: History): void => {
      const pushState = history.pushState;
      history.pushState = (...args) => {
        NProgress.done();
        return pushState.apply(history, args);
      };
    })((window as Window).history);

    ((history: History): void => {
      const replaceState = history.replaceState;
      history.replaceState = (...args) => {
        NProgress.done();
        return replaceState.apply(history, args);
      };
    })((window as Window).history);

    function handlePageHide(): void {
      NProgress.done();
    }

    function handleBackAndForth(): void {
      NProgress.done();
    }
    window.addEventListener('popstate', handleBackAndForth);
    document.addEventListener('click', handleClick);
    window.addEventListener('pagehide', handlePageHide);

    return (): void => {
      document.removeEventListener('click', handleClick);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handleBackAndForth);

      if (trickleTimeoutRef.current) {
        clearTimeout(trickleTimeoutRef.current);
      }
    };
  }, [easing, initialPosition, isSameHostName, speed, trickle, trickleSpeed, trickleStopTime]);

  return styles;
}

export default LoadingBar;